import {
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  CursorArrowRaysIcon,
} from '@heroicons/react/20/solid';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import publicCouponApi from '../../services/publicCouponApi';
import { PublicCouponType, MENU_FIELD_COPY } from './constants';
import { menuNamesLabel } from './components/coupon-menus';
import NoCoupon from '../../assets/no-coupon.svg';
import Notification from '../../components/notification/notification';
import { PageHeader, TWDataTable } from '../../components/ui';
import { headerCls, DataTableSkeleton } from '../../data/columns';

const ArchivedCampaigns = () => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);

  const [archivedList, setArchivedList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const fetchArchivedCampaigns = async () => {
    setLoading(true);
    try {
      const query = `?companyid=${globalCompany.id}&status=5&PageNumber=${pagination.pageNumber}&PageSize=${pagination.pageSize}&orderby=0&ordercolumn=status`;
      const list = await publicCouponApi.list(query);

      const responseData = list.data?.data || list.data.data || [];
      setArchivedList(responseData);
      setPagination((prev) => ({
        ...prev,
        pageNumber: list.data.pageNumber || prev.pageNumber,
        pageSize: list.data.pageSize || prev.pageSize,
        totalRecords:
          typeof list.data.totalRecords === 'number'
            ? list.data.totalRecords
            : responseData.length,
      }));
    } catch (error) {
      setNotf({
        display: true,
        type: 'error',
        message: error,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchivedCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany.id, pagination.pageNumber]);

  const renderTypeLabel = (coupon) => {
    if (coupon.type === PublicCouponType.SpendGetFreeItem)
      return 'Spend Get Free Item';
    if (coupon.type === PublicCouponType.SpendGetOff)
      return coupon.discounttype === 0
        ? `Spend Get %${coupon.discountamount || ''} Off`
        : `Spend Get $${coupon.discountamount || ''} Off`;
    if (coupon.type === PublicCouponType.BuyXGetY)
      return `Buy X Get Y${coupon.buyItemCount && coupon.getItemCount ? ` (Buy ${coupon.buyItemCount} Get ${coupon.getItemCount})` : ''}`;
    return '';
  };

  const columns = [
    {
      title: <span className={headerCls}>Campaign Name</span>,
      key: 'name',
      render: (_, coupon) => (
        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
          {coupon.name}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Code</span>,
      key: 'code',
      render: (_, coupon) => (
        <span className="text-sm text-slate-500 dark:text-slate-300">
          {coupon.code}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Type</span>,
      key: 'type',
      render: (_, coupon) => (
        <span className="text-sm text-slate-500 dark:text-slate-300">
          {renderTypeLabel(coupon)}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Platform</span>,
      key: 'platform',
      render: (_, coupon) => (
        <div className="flex items-center gap-1">
          {coupon.platforms?.includes('10') && (
            <ComputerDesktopIcon className="h-4 w-4 text-slate-500" />
          )}
          {coupon.platforms?.includes('20') && (
            <DevicePhoneMobileIcon className="h-4 w-4 text-slate-500" />
          )}
          {coupon.platforms?.includes('0') && (
            <CursorArrowRaysIcon className="h-4 w-4 text-slate-500" />
          )}
        </div>
      ),
    },
    {
      title: <span className={headerCls}>{MENU_FIELD_COPY.column}</span>,
      key: 'menus',
      render: (_, coupon) => {
        const label = menuNamesLabel(coupon.menunames);
        const restricted = label !== MENU_FIELD_COPY.any;
        return (
          <span
            title={restricted ? label : undefined}
            className={`block max-w-[12rem] truncate text-sm ${restricted ? 'text-slate-600 dark:text-slate-300' : 'text-slate-500 dark:text-slate-400'}`}
          >
            {label}
          </span>
        );
      },
    },
    {
      title: <span className={headerCls}>Status</span>,
      key: 'status',
      render: () => (
        <span className="inline-flex items-center rounded-md bg-slate-100 px-3 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
          Archived
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="p-4 sm:p-6 lg:p-8 bg-white dark:bg-slate-800">
        <div className="mx-auto max-w-5xl">
          <div className="mt-8">
            <div className="mb-4">
              <PageHeader
                title="Archived Campaigns"
                description="View archived campaigns"
                backTo="/dashboard/campaigns"
              />
            </div>

            {loading ? (
              <DataTableSkeleton
                rows={6}
                headers={[
                  { label: 'Campaign Name', w: 'w-40' },
                  { label: 'Code', w: 'w-20' },
                  { label: 'Type', w: 'w-32' },
                  { label: 'Platform', w: 'w-16' },
                  { label: MENU_FIELD_COPY.column, w: 'w-24' },
                  { label: 'Status', w: 'w-20', shape: 'pill' },
                ]}
              />
            ) : archivedList.length === 0 ? (
              <div className="mt-2 flow-root">
                <div className="min-h-[50vh] flex flex-col items-center justify-center text-center">
                  <img src={NoCoupon} className="h-24 mb-2" alt="" />
                  <p className="text-slate-400 font-light">
                    No Archived Campaigns
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-2">
                <TWDataTable
                  columns={columns}
                  data={archivedList}
                  rowKey="id"
                  pagination={
                    pagination.totalRecords > pagination.pageSize
                      ? {
                          current: pagination.pageNumber,
                          pageSize: pagination.pageSize,
                          total: pagination.totalRecords,
                          showTotal: (total, range) =>
                            `Showing ${range[0]} to ${range[1]} of ${total} archived campaigns`,
                          onChange: (page) =>
                            setPagination((prev) => ({
                              ...prev,
                              pageNumber: page,
                            })),
                          showSizeChanger: false,
                        }
                      : undefined
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default ArchivedCampaigns;
